/* 🖼️ Product Media Gallery - High-Performance Vanilla JavaScript */

(function() {
  'use strict';
  
  
  /**
   * Main Media Gallery Controller
   * Handles all gallery functionality with performance optimization
   */
  class ProductMediaGallery {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.productId = container.dataset.productId;
      
      // Core elements
      this.mediaDisplay = null;
      this.mediaItems = [];
      // Remove thumbnails for cleaner design
      this.navButtons = {};
      this.counter = null;
      
      // State management
      this.currentIndex = 0;
      this.totalItems = 0;
      this.isTransitioning = false;
      this.preloadedImages = new Set();
      
      // Touch/gesture handling
      this.touchHandler = null;
      this.keyboardHandler = null;
      
      // Performance optimization
      this.intersectionObserver = null;
      this.resizeObserver = null;
      
      this.init();
    }
    
    async init() {
      try {
        this.findElements();
        this.setupEventListeners();
        this.initializeTouchHandler();
        this.initializeKeyboardHandler();
        this.setupIntersectionObserver();
        this.preloadCriticalImages();
        
        // Gallery initialized successfully with ${this.totalItems} media items
        
        // Dispatch ready event
        this.dispatchEvent('gallery:ready', {
          currentIndex: this.currentIndex,
          totalItems: this.totalItems
        });
        
      } catch (error) {
        console.error('❌ Media Gallery initialization failed:', error);
        this.handleError(error);
      }
    }
    
    findElements() {
      // Core display elements
      this.mediaDisplay = this.container.querySelector('.media-items');
      this.mediaItems = Array.from(this.container.querySelectorAll('.media-item'));
      // Remove thumbnails for cleaner design
      
      // Navigation elements
      this.navButtons.prev = this.container.querySelector('.media-nav-prev');
      this.navButtons.next = this.container.querySelector('.media-nav-next');
      this.counter = this.container.querySelector('.media-counter');
      
      // Validation
      if (!this.mediaDisplay || this.mediaItems.length === 0) {
        throw new Error('Required media elements not found');
      }
      
      this.totalItems = this.mediaItems.length;
      
      // Update counter
      this.updateCounter();
    }
    
    setupEventListeners() {
      // Navigation button events
      if (this.navButtons.prev) {
        this.navButtons.prev.addEventListener('click', (e) => {
          e.preventDefault();
          this.goToPrevious();
        });
      }
      
      if (this.navButtons.next) {
        this.navButtons.next.addEventListener('click', (e) => {
          e.preventDefault();
          this.goToNext();
        });
      }
      
      // Thumbnail click events removed - no thumbnails
      
      // Video play button events
      this.setupVideoControls();
      
      // 3D model interaction events
      this.setup3DModelControls();
      
      // Variant change listener (for product variants affecting media)
      this.boundVariantChangeHandler = (e) => {
        this.handleVariantChange(e.detail);
      };
      document.addEventListener('variant:change', this.boundVariantChangeHandler);
      
      // Window resize handling
      this.setupResizeHandler();
    }
    
    initializeTouchHandler() {
      this.touchHandler = new MediaGalleryTouchHandler(this.mediaDisplay, {
        onSwipeLeft: () => this.goToNext(),
        onSwipeRight: () => this.goToPrevious(),
        threshold: 50,
        preventScroll: true
      });
    }
    
    initializeKeyboardHandler() {
      this.keyboardHandler = new MediaGalleryKeyboardHandler(this.container, {
        onPrevious: () => this.goToPrevious(),
        onNext: () => this.goToNext(),
        onSelect: (index) => this.goToSlide(index),
        totalItems: this.totalItems
      });
    }
    
    setupVideoControls() {
      const videoContainers = this.container.querySelectorAll('.media-video-container');
      
      videoContainers.forEach(container => {
        const video = container.querySelector('.media-video');
        const playButton = container.querySelector('.video-play-button');
        const overlay = container.querySelector('.video-play-overlay');
        
        if (video && playButton) {
          playButton.addEventListener('click', () => {
            if (video.paused) {
              video.play();
              overlay.style.opacity = '0';
            } else {
              video.pause();
              overlay.style.opacity = '1';
            }
          });
          
          video.addEventListener('play', () => {
            overlay.style.opacity = '0';
          });
          
          video.addEventListener('pause', () => {
            overlay.style.opacity = '1';
          });
          
          video.addEventListener('ended', () => {
            overlay.style.opacity = '1';
          });
        }
      });
    }
    
    setup3DModelControls() {
      const modelContainers = this.container.querySelectorAll('.media-model-container');
      
      modelContainers.forEach(container => {
        // Handle 3D viewer interaction states
        const canvas = container.querySelector('.viewer-3d-canvas');
        const viewer3DContainer = container.querySelector('.custom-3d-viewer-container');
        
        if (canvas && viewer3DContainer) {
          // Add interaction indicators
          canvas.addEventListener('mousedown', () => {
            container.classList.add('viewer-interacting');
          });
          
          canvas.addEventListener('mouseup', () => {
            container.classList.remove('viewer-interacting');
          });
          
          canvas.addEventListener('touchstart', () => {
            container.classList.add('viewer-interacting');
          });
          
          canvas.addEventListener('touchend', () => {
            container.classList.remove('viewer-interacting');
          });
          
          // Monitor for successful 3D viewer initialization
          const checkViewerReady = () => {
            if (container._3dViewer && container._3dViewer.isInitialized) {
              container.classList.add('viewer-ready');
              container.classList.remove('viewer-error');
            } else {
              // Check again after a short delay
              setTimeout(checkViewerReady, 500);
            }
          };
          
          setTimeout(checkViewerReady, 1000);
        }
      });
    }
    
    setupIntersectionObserver() {
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.preloadAdjacentImages();
            }
          });
        }, {
          threshold: 0.1,
          rootMargin: '50px'
        });
        
        this.intersectionObserver.observe(this.container);
      }
    }
    
    setupResizeHandler() {
      this.handleResize = this.handleResize.bind(this);
      this.resizeTimeout = null;
      
      this.boundResizeHandler = () => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          this.handleResize();
        }, 250);
      };
      
      window.addEventListener('resize', this.boundResizeHandler);
    }
    
    goToSlide(index, direction = 'auto') {
      if (this.isTransitioning || index < 0 || index >= this.totalItems || index === this.currentIndex) {
        return;
      }
      
      this.isTransitioning = true;
      
      // Calculate direction if auto
      if (direction === 'auto') {
        direction = index > this.currentIndex ? 'next' : 'previous';
      }
      
      // Update active states
      this.updateActiveStates(index);
      
      // Perform transition
      this.performTransition(this.currentIndex, index, direction);
      
      // Update current index
      this.currentIndex = index;
      
      // Update navigation states
      this.updateNavigationStates();
      
      // Update counter
      this.updateCounter();
      
      // Preload adjacent images
      this.preloadAdjacentImages();
      
      // Dispatch change event
      this.dispatchEvent('gallery:change', {
        currentIndex: this.currentIndex,
        totalItems: this.totalItems,
        direction: direction
      });
      
      // Reset transition flag after animation
      setTimeout(() => {
        this.isTransitioning = false;
      }, 300);
    }
    
    goToNext() {
      if (this.currentIndex < this.totalItems - 1) {
        this.goToSlide(this.currentIndex + 1, 'next');
      } else if (this.totalItems > 1) {
        // Loop to first slide
        this.goToSlide(0, 'next');
      }
    }
    
    goToPrevious() {
      if (this.currentIndex > 0) {
        this.goToSlide(this.currentIndex - 1, 'previous');
      } else if (this.totalItems > 1) {
        // Loop to last slide
        this.goToSlide(this.totalItems - 1, 'previous');
      }
    }
    
    performTransition(fromIndex, toIndex, direction) {
      const fromItem = this.mediaItems[fromIndex];
      const toItem = this.mediaItems[toIndex];
      
      if (!fromItem || !toItem) return;
      
      // Hide current item
      fromItem.classList.remove('active');
      fromItem.style.display = 'none';
      
      // Show new item
      toItem.style.display = 'block';
      
      // Force reflow for smooth transition
      toItem.offsetHeight;
      
      // Add active class
      toItem.classList.add('active');
      
      // Pause any videos not in current view
      this.pauseInactiveVideos(toIndex);
    }
    
    updateActiveStates(newIndex) {
      // Update media items
      this.mediaItems.forEach((item, index) => {
        item.classList.toggle('active', index === newIndex);
        item.setAttribute('aria-hidden', index !== newIndex);
      });
      
      // Thumbnails removed - no thumbnail updates needed
    }
    
    updateNavigationStates() {
      if (this.navButtons.prev) {
        this.navButtons.prev.disabled = false; // Always enabled for looping
      }
      
      if (this.navButtons.next) {
        this.navButtons.next.disabled = false; // Always enabled for looping
      }
    }
    
    updateCounter() {
      if (this.counter) {
        const currentSpan = this.counter.querySelector('.media-current');
        if (currentSpan) {
          currentSpan.textContent = this.currentIndex + 1;
        }
      }
    }
    
    preloadCriticalImages() {
      // Preload first 2 images for instant display
      for (let i = 0; i < Math.min(2, this.totalItems); i++) {
        this.preloadImage(i);
      }
    }
    
    preloadAdjacentImages() {
      // Preload previous and next images
      const indicesToPreload = [
        this.currentIndex - 1,
        this.currentIndex + 1
      ].filter(index => index >= 0 && index < this.totalItems);
      
      indicesToPreload.forEach(index => this.preloadImage(index));
    }
    
    preloadImage(index) {
      if (this.preloadedImages.has(index) || !this.mediaItems[index]) {
        return;
      }
      
      const mediaItem = this.mediaItems[index];
      const img = mediaItem.querySelector('.media-image-sophisticated');
      
      if (img && img.dataset.src) {
        const preloadImg = new Image();
        preloadImg.onload = () => {
          img.src = img.dataset.src;
          this.preloadedImages.add(index);
        };
        preloadImg.onerror = () => {
          // Failed to preload image at index ${index} - will load on demand
        };
        preloadImg.src = img.dataset.src;
      } else {
        this.preloadedImages.add(index);
      }
    }
    
    pauseInactiveVideos(currentIndex) {
      this.mediaItems.forEach((item, index) => {
        if (index !== currentIndex) {
          const video = item.querySelector('.media-video');
          if (video && !video.paused) {
            video.pause();
          }
        }
      });
    }
    
    handleVariantChange(variantData) {
      // Handle media changes when product variant changes
      if (variantData && variantData.featured_media) {
        const mediaId = variantData.featured_media.id;
        const targetIndex = this.mediaItems.findIndex(item => 
          item.dataset.mediaId === mediaId.toString()
        );
        
        if (targetIndex !== -1 && targetIndex !== this.currentIndex) {
          this.goToSlide(targetIndex);
        }
      }
    }
    
    handleResize() {
      // Handle responsive changes
      this.updateThumbnailVisibility();
    }
    
    updateThumbnailVisibility() {
      // Thumbnails removed - no visibility updates needed
    }
    
    handleError(error) {
      console.error('Media Gallery Error:', error);
      
      // Dispatch error event
      this.dispatchEvent('gallery:error', {
        error: error.message,
        currentIndex: this.currentIndex
      });
    }
    
    dispatchEvent(eventName, detail = {}) {
      const event = new CustomEvent(eventName, {
        detail: {
          gallery: this,
          ...detail
        },
        bubbles: true,
        cancelable: true
      });
      
      this.container.dispatchEvent(event);
    }
    
    // Public API methods
    getCurrentIndex() {
      return this.currentIndex;
    }
    
    getTotalItems() {
      return this.totalItems;
    }
    
    goTo(index) {
      this.goToSlide(index);
    }
    
    next() {
      this.goToNext();
    }
    
    previous() {
      this.goToPrevious();
    }
    
    destroy() {
      // Clean up touch and keyboard handlers
      if (this.touchHandler) {
        this.touchHandler.destroy();
        this.touchHandler = null;
      }
      
      if (this.keyboardHandler) {
        this.keyboardHandler.destroy();
        this.keyboardHandler = null;
      }
      
      // Clean up observers
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
      
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      
      // Clean up event listeners
      if (this.boundResizeHandler) {
        window.removeEventListener('resize', this.boundResizeHandler);
        this.boundResizeHandler = null;
      }
      
      if (this.boundVariantChangeHandler) {
        document.removeEventListener('variant:change', this.boundVariantChangeHandler);
        this.boundVariantChangeHandler = null;
      }
      
      // Clear timeouts
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = null;
      }
      
      // Clear preloaded images
      if (this.preloadedImages) {
        this.preloadedImages.clear();
        this.preloadedImages = null;
      }
      
      // Media Gallery destroyed and cleaned up
    }
  }
  
  /**
   * Touch Handler for Swipe Gestures
   */
  class MediaGalleryTouchHandler {
    constructor(element, options = {}) {
      this.element = element;
      this.options = {
        threshold: 50,
        preventScroll: true,
        onSwipeLeft: () => {},
        onSwipeRight: () => {},
        ...options
      };
      
      this.startX = 0;
      this.startY = 0;
      this.currentX = 0;
      this.currentY = 0;
      this.isTracking = false;
      
      this.init();
    }
    
    init() {
      // Passive event listeners for better performance
      this.element.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: !this.options.preventScroll });
      this.element.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: !this.options.preventScroll });
      this.element.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
      this.element.addEventListener('touchcancel', this.handleTouchCancel.bind(this), { passive: true });
    }
    
    handleTouchStart(e) {
      if (e.touches.length !== 1) return;

      // Let the native 3D viewer own drag gestures so the model remains interactive.
      if (e.target.closest && e.target.closest('model-viewer')) {
        this.isTracking = false;
        return;
      }
      
      const touch = e.touches[0];
      this.startX = touch.clientX;
      this.startY = touch.clientY;
      this.isTracking = true;
    }
    
    handleTouchMove(e) {
      if (!this.isTracking || e.touches.length !== 1) return;
      
      const touch = e.touches[0];
      this.currentX = touch.clientX;
      this.currentY = touch.clientY;
      
      // Prevent default if horizontal swipe is detected
      if (this.options.preventScroll && this.isHorizontalSwipe()) {
        e.preventDefault();
      }
    }
    
    handleTouchEnd() {
      if (!this.isTracking) return;
      
      const deltaX = this.currentX - this.startX;
      const deltaY = this.currentY - this.startY;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);
      
      // Ensure this is a horizontal swipe
      if (absDeltaX > this.options.threshold && absDeltaX > absDeltaY) {
        if (deltaX > 0) {
          this.options.onSwipeRight();
        } else {
          this.options.onSwipeLeft();
        }
      }
      
      this.reset();
    }
    
    handleTouchCancel() {
      this.reset();
    }
    
    isHorizontalSwipe() {
      const deltaX = Math.abs(this.currentX - this.startX);
      const deltaY = Math.abs(this.currentY - this.startY);
      return deltaX > deltaY && deltaX > 10;
    }
    
    reset() {
      this.isTracking = false;
      this.startX = 0;
      this.startY = 0;
      this.currentX = 0;
      this.currentY = 0;
    }
    
    destroy() {
      this.element.removeEventListener('touchstart', this.handleTouchStart);
      this.element.removeEventListener('touchmove', this.handleTouchMove);
      this.element.removeEventListener('touchend', this.handleTouchEnd);
      this.element.removeEventListener('touchcancel', this.handleTouchCancel);
    }
  }
  
  /**
   * Keyboard Handler for Accessibility
   */
  class MediaGalleryKeyboardHandler {
    constructor(element, options = {}) {
      this.element = element;
      this.options = {
        onPrevious: () => {},
        onNext: () => {},
        onSelect: (index) => {},
        totalItems: 0,
        ...options
      };
      
      this.currentThumbnailIndex = 0;
      
      this.init();
    }
    
    init() {
      this.element.addEventListener('keydown', this.handleKeydown.bind(this));
      
      // Make gallery focusable
      if (!this.element.hasAttribute('tabindex')) {
        this.element.setAttribute('tabindex', '0');
      }
    }
    
    handleKeydown(e) {
      // Only handle when gallery is focused
      if (document.activeElement !== this.element && !this.element.contains(document.activeElement)) {
        return;
      }
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.options.onPrevious();
          break;
          
        case 'ArrowRight':
          e.preventDefault();
          this.options.onNext();
          break;
          
        case 'Home':
          e.preventDefault();
          this.options.onSelect(0);
          break;
          
        case 'End':
          e.preventDefault();
          this.options.onSelect(this.options.totalItems - 1);
          break;
          
        case 'Enter':
        case ' ':
          // Handle thumbnail selection
          if (document.activeElement.classList.contains('thumbnail-item')) {
            e.preventDefault();
            const index = Array.from(document.activeElement.parentNode.children).indexOf(document.activeElement);
            this.options.onSelect(index);
          }
          break;
      }
    }
    
    destroy() {
      this.element.removeEventListener('keydown', this.handleKeydown);
    }
  }
  
  /**
   * Gallery Manager - Handles multiple gallery instances
   */
  class MediaGalleryManager {
    constructor() {
      this.galleries = new Map();
      this.init();
    }
    
    init() {
      this.initializeGalleries();
      this.setupGlobalListeners();
    }
    
    initializeGalleries() {
      const galleryContainers = document.querySelectorAll('.product-media-gallery');
      
      galleryContainers.forEach(container => {
        const sectionId = container.dataset.sectionId;
        if (sectionId && !this.galleries.has(sectionId)) {
          const gallery = new ProductMediaGallery(container);
          this.galleries.set(sectionId, gallery);
        }
      });
      
      // Initialized ${this.galleries.size} media galleries
    }
    
    setupGlobalListeners() {
      // Listen for Shopify section reloads
      document.addEventListener('shopify:section:load', (e) => {
        if (e.detail.sectionId) {
          this.reinitializeGallery(e.detail.sectionId);
        }
      });
      
      // Listen for section unloads
      document.addEventListener('shopify:section:unload', (e) => {
        if (e.detail.sectionId) {
          this.destroyGallery(e.detail.sectionId);
        }
      });
    }
    
    reinitializeGallery(sectionId) {
      // Destroy existing gallery
      this.destroyGallery(sectionId);
      
      // Find and initialize new gallery
      const container = document.querySelector(`[data-section-id="${sectionId}"]`);
      if (container && container.classList.contains('product-media-gallery')) {
        const gallery = new ProductMediaGallery(container);
        this.galleries.set(sectionId, gallery);
      }
    }
    
    destroyGallery(sectionId) {
      if (this.galleries.has(sectionId)) {
        this.galleries.get(sectionId).destroy();
        this.galleries.delete(sectionId);
      }
    }
    
    getGallery(sectionId) {
      return this.galleries.get(sectionId);
    }
    
    getAllGalleries() {
      return Array.from(this.galleries.values());
    }
  }
  
  // Initialize when DOM is ready
  function initializeMediaGalleries() {
    if (typeof window.ProductMediaGalleryManager === 'undefined') {
      window.ProductMediaGalleryManager = new MediaGalleryManager();
      
      // Expose classes for external use
      window.ProductMediaGallery = ProductMediaGallery;
      window.MediaGalleryTouchHandler = MediaGalleryTouchHandler;
      window.MediaGalleryKeyboardHandler = MediaGalleryKeyboardHandler;
    }
  }
  
  // DOM Ready check
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeMediaGalleries);
  } else {
    initializeMediaGalleries();
  }
  
})();
